import { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useInView,
  animate,
} from "framer-motion";
import { ArrowRight, ShieldCheck, Timer, CreditCard } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
  "https://images.pexels.com/photos/28556690/pexels-photo-28556690.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LINES = [
  [{ t: "The breakroom" }],
  [{ t: "upgrade that" }],
  [{ t: "costs you " }, { t: "nothing.", accent: true }],
];

const CHIPS = [
  { icon: ShieldCheck, label: "$0 equipment cost", pos: "-left-4 top-10 sm:-left-8", delay: 0 },
  { icon: Timer, label: "Same-day tech dispatch", pos: "-right-3 top-1/3 sm:-right-6", delay: 0.9 },
  { icon: CreditCard, label: "100% cashless & NFC", pos: "left-6 bottom-32", delay: 1.7 },
];

const STATS = [
  { value: 24, suffix: "/7", label: "Machine monitoring & support", testid: "stat-metric-0" },
  { value: 100, suffix: "%", label: "Cashless — tap card or phone", testid: "stat-metric-1" },
  { value: 100, suffix: "%", label: "Same-day service guarantee on every machine", testid: "stat-metric-2" },
  { display: "1–3", label: "Business days — max for your tailored proposal", testid: "stat-metric-3" },
];

function Stat({ value, prefix = "", suffix = "", decimals = 0, label, testid, display }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [count, setCount] = useState((0).toFixed(decimals));
  useEffect(() => {
    if (!inView || display !== undefined) return;
    const controls = animate(0, value, {
      duration: 1.6,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setCount(v.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, value, decimals, display]);
  return (
    <div ref={ref} data-testid={testid} className="px-6 py-8 text-center lg:py-10">
      <div className="font-display text-4xl font-extrabold tracking-tight text-[#16120E] sm:text-5xl">
        {prefix}
        {display !== undefined ? display : count}
        <span className="text-[#A8861D]">{suffix}</span>
      </div>
      <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-stone-500 sm:text-[13px]">
        {label}
      </p>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 15 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 60, damping: 15 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} className="relative overflow-hidden">
      <div className="bg-grid absolute inset-0" aria-hidden="true" />
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-[#1B365D]/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-[-15%] h-[420px] w-[420px] rounded-full bg-[#C9A227]/20 blur-[130px]"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 pt-32 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pb-24 lg:pt-40">
        <div className="lg:col-span-6 xl:col-span-6">
          <motion.span
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-2.5 rounded-full border border-black/10 bg-white/70 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-[#A8861D]"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#A8861D] opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#A8861D]" />
            </span>
            24/7 full-service vending operator
          </motion.span>

          <h1
            data-testid="hero-main-title"
            className="mt-7 font-display text-[clamp(2.7rem,6.4vw,5.4rem)] font-extrabold leading-[1.02] tracking-[-0.02em] text-[#16120E]"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.18 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.map((w, j) =>
                    w.accent ? (
                      <span key={j} className="relative inline-block text-[#1B365D]">
                        {w.t}
                        <svg
                          className="absolute -bottom-1 left-0 w-full"
                          viewBox="0 0 220 12"
                          fill="none"
                          aria-hidden="true"
                        >
                          <motion.path
                            d="M4 9C60 3 160 3 216 8"
                            stroke="#C9A227"
                            strokeWidth="4"
                            strokeLinecap="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.7, delay: 1.1, ease: "easeOut" }}
                          />
                        </svg>
                      </span>
                    ) : (
                      <span key={j}>{w.t}</span>
                    ),
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-stone-600 sm:text-lg"
          >
            SnackSavvy installs, stocks and manages smart vending machines, micro
            markets and café-grade coffee in your workplace. Free equipment, zero
            effort, 24/7 support — you just pick the snacks.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-request-quote-button"
              onClick={() => scrollToSection("quote")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#1B365D] px-7 py-4 text-base font-semibold text-[#FBF7EF] transition-all hover:-translate-y-0.5 hover:bg-[#2A4A73] hover:shadow-[0_16px_44px_rgba(27,54,93,0.4)]"
            >
              Get your free quote
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-explore-machines-button"
              onClick={() => scrollToSection("machines")}
              className="inline-flex items-center gap-2.5 rounded-full border border-[#C9A227]/50 px-7 py-4 text-base font-semibold text-[#A8861D] transition-all hover:-translate-y-0.5 hover:border-[#C9A227]"
            >
              Explore the machines
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            style={{ y: imgY, rotateX, rotateY, transformPerspective: 1100 }}
            onMouseMove={onTilt}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            className="relative mx-auto max-w-[520px]"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-[2rem] border border-black/10 shadow-[0_50px_140px_rgba(27,54,93,0.16)]"
            >
              <img
                src={HERO_IMG}
                alt="SnackSavvy SnackPod smart vending machine glowing in a dark office"
                className="h-[420px] w-full object-cover sm:h-[520px] lg:h-[580px]"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(70% 55% at 50% 32%, rgba(27,54,93,0.12), transparent 70%)",
                }}
              />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-44"
                style={{
                  background: "linear-gradient(to top, rgba(11,15,23,0.92), transparent)",
                }}
              />
              <div className="absolute inset-x-5 bottom-5 flex items-center justify-between rounded-2xl border border-white/20 bg-black/40 px-5 py-4 backdrop-blur-xl">
                <div>
                  <p className="font-display text-sm font-bold text-white">SnackPod™ One</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
                    Live inventory · restocked 06:00
                  </p>
                </div>
                <span className="flex items-center gap-2 rounded-full bg-[#1B365D]/60 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-[#FBF7EF]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#FBF7EF]" />
                  Online
                </span>
              </div>
            </motion.div>

            {CHIPS.map((chip) => (
              <motion.div
                key={chip.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1, y: [0, -9, 0] }}
                transition={{
                  opacity: { duration: 0.5, delay: 1.2 + chip.delay * 0.2 },
                  scale: { duration: 0.5, delay: 1.2 + chip.delay * 0.2 },
                  y: { duration: 5.5, repeat: Infinity, delay: chip.delay, ease: "easeInOut" },
                }}
                className={`absolute ${chip.pos} hidden items-center gap-2.5 rounded-2xl border border-black/10 bg-white/90 px-4 py-3 backdrop-blur-xl sm:flex`}
              >
                <chip.icon className="h-4 w-4 text-[#1B365D]" />
                <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-[#16120E]">
                  {chip.label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-5 pb-20 sm:px-8" data-testid="impact-stats-section">
        <div className="grid grid-cols-2 divide-x divide-black/10 overflow-hidden rounded-3xl border border-black/10 bg-white/70 lg:grid-cols-4">
          {STATS.map((s) => (
            <Stat key={s.testid} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}
